<script setup lang="ts">
import { breakpointsTailwind, clamp, useMagicKeys } from "@vueuse/core";

import type { VideoPlayer } from "#components";
import type { IBasicVideo, IEvent, IPlaylist, IPlaylistItem, ITelestration } from "@/types";
import type { IPixellotPlayerSourceType, IPixellotPlayerVideoClipOptions, IPlayerState } from "~~/modules/pixellot-sdk";
import { UModalConfirm } from "#components";
import { PLAYLIST_TYPE, SPORT_TYPES_WITHOUT_STATS } from "~/constants";
import TelestrationSidePanel from "~/videos/components/Video/TelestrationSidePanel.vue";
import { getClippedItems, type IVideoClippedListItem, isClippedVideo, resolveClippedEventForPlaylistItem } from "~/videos/helpers/get-angles-value";
import { isClip, isEvent, isHighlight, isPlaylist } from "~/videos/helpers/is-video-type";
import { VIDEO_SOURCE_TYPE } from "~/videos/types/video";

import type { VideoJsPlayer as IPixellotPlayer, ITag as IPixellotTag, ITagGroup as IPixellotTagGroup, IPlayerControlLink } from "@pixellot/web-sdk";

import type { IPlayerData } from "./Player.vue";

const FLOATING_PLAYER_MODE = {
  DEFAULT: "default",
  CLIPPED: "clipped",
  PLAYLIST: "playlist",
} as const;
type TFloatingPlayerMode = (typeof FLOATING_PLAYER_MODE)[keyof typeof FLOATING_PLAYER_MODE];

const { t, locale } = useI18n();
const toast = useToast();
const modal = useModal();
const route = useRoute();
const router = useRouter();
const user = useUser();
const segment = useSegment();
const telestrationStore = useTelestrationStore();
const shapesStore = useShapesStore();
const isOpen = ref(false);
const [isInPiPMode, togglePiPMode] = useToggle(false);
const breakpoints = useBreakpoints(breakpointsTailwind);
const greaterOrEqualLg = breakpoints.greaterOrEqual("lg");
const isInNativePiPMode = ref<boolean>(false);
const video = ref<IEvent | IBasicVideo | null>(null);
/** Catalog event for clipped mode; list rows use per-clip data from `getClippedItems` (see `eventFromClippedPlaybackSource`). */
const baseEvent = ref<IEvent | null>(null);
const playlist = ref<IPlaylist | null>(null);
const videoPlayerRef = ref<InstanceType<typeof VideoPlayer> | null>(null);
const modalRootEl = ref<HTMLElement | null>(null);
const modalHeaderEl = ref<HTMLElement | null>(null);
const playlistItems = ref<IPlaylistItem[]>([]);
const activePlaylistItem = ref<IPlaylistItem | null>(null);
const isEditingPlaylistItem = ref(false);
const isDeleted = ref<boolean>(false);

const playerInstance = computed<IPixellotPlayer | null>(() => (videoPlayerRef.value ? videoPlayerRef.value.player : null));
const playerError = computed<boolean>(() => (videoPlayerRef.value ? videoPlayerRef.value.error : false));
const playerState = computed<IPlayerState | null>(() => (videoPlayerRef.value ? videoPlayerRef.value.state : null));

const clippedItems = ref<IVideoClippedListItem[]>([]);
const activeClippedItem = ref<IVideoClippedListItem | null>(null);
/** Sidebar + wide layout + prev/next clips (not PiP sidebar visibility — see `canShowClippedSidebar`). */
const hasClippedVideoItems = computed(() => clippedItems.value.length > 0);

const telestrationTags = computed<IPixellotTag[]>(() => {
  if (!activePlaylistItem.value) return [];

  const playlistItem = activePlaylistItem.value;
  const telestrations = playlistItem.telestrations || [];

  const tags = telestrations.map<IPixellotTag>(({ start, id }) => ({
    id: id,
    time: (start || 0) - playlistItem.startTime,
    representation: "telestration",
  }));

  return tags.sort((a, b) => (a.time || 0) - (b.time || 0));
});

const canShowPlaylistSidebar = computed(() => {
  if (telestrationStore.telestrationMode === TelestrationMode.EDIT || isInPiPMode.value) return false;
  return Boolean(playlist.value && playlistItems.value.length > 0);
});

const canShowClippedSidebar = computed(() => {
  if (isInPiPMode.value) return false;
  return hasClippedVideoItems.value;
});

const controlLink = computed<IPlayerControlLink | undefined>(() => {
  const vid = unref(video);
  const isEventPage = route.path.startsWith(`/events/${vid?.eventId}`);

  if (isEvent(vid)) {
    if (playlist.value) {
      return {
        target: "_blank" as const,
        label: `${vid.game_info.team1_name} vs ${vid.game_info.team2_name}`,
        url: `/events/editor/${vid.eventId}`,
      };
    }

    if (SPORT_TYPES_WITHOUT_STATS.includes((video.value as IEvent)?.sport_type)) {
      return undefined;
    }

    return {
      target: "_self" as const,
      label: t("labels.open_in_editor"),
      url: `/events/editor/${vid.eventId}`,
    };
  } else if ((isClip(vid) || isHighlight(vid)) && !isEventPage) {
    return {
      target: "_self" as const,
      label: t("labels.go_to_video_page"),
      url: `/events/${vid.eventId}`,
    };
  }

  return undefined;
});

const { arrowup, arrowdown, escape: escapeKey } = useMagicKeys();
const playerReadyCallbacks = ref<((player: IPixellotPlayer) => void)[]>([]);

const cachedPiPPosition = useSessionStorage("player-pip-position", {
  x: 40,
  y: 40,
});

const { x, y, isDragging } = useDraggable(modalRootEl, {
  preventDefault: true,
  stopPropagation: true,
  handle: modalHeaderEl,
  initialValue: cachedPiPPosition.value,
  onEnd: onDragEnd,
});

const { left, right } = useElementBounding(window.document.body);
const { width, height } = useElementBounding(modalRootEl, { immediate: false });
const restrictedX = computed(() => clamp(left.value, x.value, right.value - width.value));
const restrictedY = computed(() => clamp(0, y.value, window.innerHeight - height.value));

const upNextBannerDefaultSeconds = 5;

/** Prev/next indices for clipped list or playlist (mutually exclusive). */
const playbackNav = computed(() => {
  if (hasClippedVideoItems.value && clippedItems.value.length) {
    const list = clippedItems.value;
    const currentIndex = list.findIndex((item) => item.id === activeClippedItem.value?.id);

    return {
      mode: FLOATING_PLAYER_MODE.CLIPPED,
      nextIndex: currentIndex + 1,
      previousIndex: currentIndex - 1,
      currentIndex,
    };
  }

  const list = playlistItems.value;
  if (!list?.length) return null;

  const currentIndex = list.findIndex((item) => item.id === activePlaylistItem.value?.id);

  return {
    mode: FLOATING_PLAYER_MODE.PLAYLIST,
    nextIndex: currentIndex + 1,
    previousIndex: currentIndex - 1,
    currentIndex,
  };
});

const nextListItem = computed<IVideoClippedListItem | IPlaylistItem | null>(() => {
  const nav = playbackNav.value;
  if (!nav) return null;
  if (nav.mode === FLOATING_PLAYER_MODE.CLIPPED) {
    return clippedItems.value[nav.nextIndex] ?? null;
  }
  return playlistItems.value[nav.nextIndex] ?? null;
});

const previousListItem = computed<IVideoClippedListItem | IPlaylistItem | null>(() => {
  const nav = playbackNav.value;
  if (!nav) return null;
  if (nav.mode === FLOATING_PLAYER_MODE.CLIPPED) {
    return clippedItems.value[nav.previousIndex] ?? null;
  }
  return playlistItems.value[nav.previousIndex] ?? null;
});

const isShowUpNextBanner = computed(() => {
  if (!nextListItem.value || !playerState.value?.currentTime) return false;

  if (activePlaylistItem.value?.endTime) {
    return activePlaylistItem.value?.startTime + playerState.value.currentTime >= activePlaylistItem.value?.endTime - upNextBannerDefaultSeconds;
  }

  const dur = activeClippedItem.value?.video_duration;
  if (dur) {
    return playerState.value.currentTime >= dur - upNextBannerDefaultSeconds;
  }

  return false;
});

function onListItemClick(type: TFloatingPlayerMode, item: IVideoClippedListItem | IPlaylistItem | null) {
  if (!item) return toast.error(t("errors.something_went_wrong"));

  if (type === FLOATING_PLAYER_MODE.CLIPPED) {
    activeClippedItem.value = item as IVideoClippedListItem;
    video.value = item.event;
  } else {
    activePlaylistItem.value = item as IPlaylistItem;
    const src = activePlaylistItem.value.playlist_source;
    if (isClippedVideo(src)) {
      baseEvent.value = src;
      video.value = resolveClippedEventForPlaylistItem(src, activePlaylistItem.value);
    } else {
      baseEvent.value = null;
      video.value = src;
    }
  }
}

/** Shared by seek buttons, keyboard arrows, and up-next — uses `nextListItem` / `previousListItem`. */
function goToNextListItem(type: TFloatingPlayerMode) {
  const next = nextListItem.value;
  if (!next) return;

  if (video.value) {
    const track =
      type === FLOATING_PLAYER_MODE.CLIPPED
        ? { message: "Moved to Next Clip", data: formatTrackVideo(video.value) }
        : { message: "Moved to Next Tag", data: formatTrackPlaylistItem(next as IPlaylistItem) };

    segment.track(track.message as keyof ISegmentTracks, track.data);
  }
  onListItemClick(type, next);
}

function goToPreviousListItem(type: TFloatingPlayerMode) {
  const prev = previousListItem.value;
  if (!prev) return;

  if (video.value) {
    const track =
      type === FLOATING_PLAYER_MODE.CLIPPED
        ? { message: "Moved to Previous Clip", data: formatTrackVideo(video.value) }
        : { message: "Moved to Previous Tag", data: formatTrackPlaylistItem(prev as IPlaylistItem) };

    segment.track(track.message as keyof ISegmentTracks, track.data);
  }

  onListItemClick(type, prev);
}

const seekButtonsConfig = computed(() => {
  const nav = playbackNav.value;

  if (nav?.mode === FLOATING_PLAYER_MODE.CLIPPED) {
    return {
      isActive: true,
      isPreviousDisabled: nav.previousIndex < 0,
      isNextDisabled: nav.nextIndex > clippedItems.value.length - 1,
      onClickNext: () => goToNextListItem(FLOATING_PLAYER_MODE.CLIPPED),
      onClickPrevious: () => goToPreviousListItem(FLOATING_PLAYER_MODE.CLIPPED),
    };
  }

  if (nav?.mode === FLOATING_PLAYER_MODE.PLAYLIST) {
    const items = playlistItems.value;

    return {
      isActive: !!items?.length,
      isPreviousDisabled: isEditingPlaylistItem.value || nav.previousIndex < 0,
      isNextDisabled: isEditingPlaylistItem.value || nav.nextIndex > items.length - 1,
      onClickNext: () => goToNextListItem(FLOATING_PLAYER_MODE.PLAYLIST),
      onClickPrevious: () => goToPreviousListItem(FLOATING_PLAYER_MODE.PLAYLIST),
    };
  }

  return {
    isActive: !!playlistItems.value.length,
    isPreviousDisabled: true,
    isNextDisabled: true,
    onClickNext: () => {},
    onClickPrevious: () => {},
  };
});

const currentVideoClipOptions = computed<IPixellotPlayerVideoClipOptions | undefined>(() => {
  if (isEditingPlaylistItem.value) return undefined;

  if (hasClippedVideoItems.value && activeClippedItem.value) {
    return undefined;
  }

  if (activePlaylistItem.value) {
    if (activePlaylistItem.value.permissions.canUseSourceURLInsteadOfSourceClipping) {
      // If the playlist item has source URL instead of source clipping, we don't need to return any clip options
      // The source URL is purely a new video clip url that we can play directly comparing to older approach of using realtime clipping from start to end
      return undefined;
    }

    const options: IPixellotPlayerVideoClipOptions = {
      id: activePlaylistItem.value.id,
      start: activePlaylistItem.value.startTime,
      end: activePlaylistItem.value.endTime,
      // Restart on last item?
      // restart_beginning: !nextListItem.value
    };
    return options;
  }
  return undefined;
});

const isTelestrationButtonShown = computed(() => {
  if (!user.value || !playlist.value) return false;

  if (playlist.value.type === PLAYLIST_TYPE.SYSTEM) return false;

  const isShared = route.query.utm_medium === "share-playlist";

  return !isShared;
});

async function open(
  vid: IPlaylist | IEvent | IBasicVideo | null,
  options?: {
    event?: IEvent;
  }
) {
  if (!vid) {
    toast.error(t("labels.unknown_video"));
    close();
    return;
  }

  isOpen.value = true;
  if (isPlaylist(vid)) {
    baseEvent.value = null;
    const accessToken = route.query.accessToken as string;
    const items = await getPlaylistItems(vid.id, { accessToken, locale: locale.value, event: options?.event }).catch((err) => {
      onError(err);
      return [];
    });
    playlistItems.value = items;
    playlist.value = { ...vid, size: vid.size || items.length };

    if (items.length === 0) {
      toast.error(t("errors.navigator_playlist_error"));
      close();
      return;
    }

    onListItemClick(FLOATING_PLAYER_MODE.PLAYLIST, items.find((item) => activePlaylistItem.value?.id === item.id) || items[0]);
    togglePiPMode(false);

    // Omitting saving system playlist id into query since after reload the fetch will not work for those
    // TODO: Check with BE if we can make it work
    if (vid.type !== "system") router.replace({ query: { ...route.query, playlistId: vid.id } });
    return;
  }

  if (isEvent(vid) && vid.upload_source_type === VIDEO_SOURCE_TYPE.CLIPPED) {
    telestrationStore.exitTelestrationMode();
    playlist.value = null;
    playlistItems.value = [];
    activePlaylistItem.value = null;
    baseEvent.value = vid;
    clippedItems.value = getClippedItems(vid, undefined, (index: number) => `${t("labels.clip")} ${index + 1}`);

    if (!clippedItems.value?.length) {
      // fall through to default open
      toast.error(t("errors.something_went_wrong"));
      close();
      return;
    }

    onListItemClick(FLOATING_PLAYER_MODE.CLIPPED, clippedItems.value[0]);
    togglePiPMode(false);

    router.replace({
      query: { ...route.query, videoType: vid.type, videoId: vid.id },
    });
    return;
  }

  video.value = vid;

  // togglePiPMode(video.value && vid.id === video.value.id);
  togglePiPMode(false);
  router.replace({
    query: { ...route.query, videoType: vid.type, videoId: vid.id },
  });
}

watch(
  isOpen,
  (value) => {
    if (value) {
      document.documentElement.classList.add("overflow-hidden");
    } else {
      document.documentElement.classList.remove("overflow-hidden");
    }
  },
  { immediate: true }
);

watch(
  () => telestrationStore.telestrationMode,
  (_mode, prevMode) => {
    if (prevMode === TelestrationMode.EDIT && activePlaylistItem.value?.playlistId) {
      const accessToken = route.query.accessToken as string;
      getPlaylistItems(activePlaylistItem.value?.playlistId, { accessToken, locale: locale.value })
        .then((items) => {
          playlistItems.value = items;
          activePlaylistItem.value = items.find((item) => item.id === activePlaylistItem.value?.id) || null;
        })
        .catch((err) => {
          onError(err);
          return [];
        });
    }
  }
);

// This watcher hides telestration overlays only when the video timeline meaningfully changes.
// Intent:
// - Exit telestration when a user moves in time (scrubs/seeks) or when clicking another telestration tag on the timeline
//   which results in a noticeable jump in currentTime.
// - Do NOT exit telestration when switching between telestrations via a tag's dropdown menu,
//   where the UI changes but the timeline may remain effectively the same.
// The 1s threshold filters out tiny currentTime fluctuations and avoids closing the overlay for non-timeline UI actions.
watch(
  () => playerState.value?.currentTime,
  (t = 0, prev = 0) => {
    const timeDiff = Math.abs(t - prev);
    const multipleTagsTimeGap = 1;
    if (timeDiff >= multipleTagsTimeGap) {
      telestrationStore.exitTelestrationMode();
    }
  }
);

function close() {
  if (telestrationStore.telestrationMode !== TelestrationMode.EDIT) {
    onClose();
    return;
  }

  // Don't show confirmation modal if user clicked outside of the video when was drawing or updating a shape
  if (shapesStore.isDrawing || shapesStore.isUpdating) {
    return;
  }

  // Check if there are unsaved changes before exiting telestration mode
  if (useShapesStore().historyState.canUndo) {
    modal.open(UModalConfirm, {
      heading: t("pages.playlists.telestrations.telestration_exit_confirm"),
      icon: "telestration",
      variant: "delete",
      description: t("pages.playlists.telestrations.telestration_exit_heading"),
      confirmFn: () => {
        telestrationStore.exitTelestrationMode();
        onClose();
      },
      confirmLabel: t("labels.exit"),
      cancelLabel: t("labels.cancel"),
    });
  } else {
    onClose();
  }

  function onClose() {
    isOpen.value = false;
    video.value = null;
    togglePiPMode(false);
    isInNativePiPMode.value = false;
    router.replace({
      query: {
        ...route.query,
        videoType: undefined,
        videoId: undefined,
        playlistId: undefined,
      },
    });
    playlist.value = null;
    playlistItems.value = [];
    activePlaylistItem.value = null;
    clippedItems.value = [];
    activeClippedItem.value = null;
    isEditingPlaylistItem.value = false;
    baseEvent.value = null;
  }
}

function onClippedAngleChange(angleType: IPixellotPlayerSourceType) {
  const base = baseEvent.value ?? video.value;
  if (!base || !isClippedVideo(base)) return;

  const sources = getClippedItems(base, angleType, (i: number) => `${t("labels.clip")} ${i + 1}`);
  if (!sources.length) return toast.error(t("errors.something_went_wrong"));

  clippedItems.value = sources;

  const first = sources[0];
  if (!first) return toast.error(t("errors.something_went_wrong"));
  onListItemClick(FLOATING_PLAYER_MODE.CLIPPED, first);
}

async function onDelete() {
  if (!playlist.value && isEvent(video.value)) {
    await navigateTo("/events");
  }
  close();
  isDeleted.value = true;
}

function onDragEnd(position: { x: number; y: number }) {
  cachedPiPPosition.value.x = position.x;
  cachedPiPPosition.value.y = position.y;
}

function onError(err: unknown) {
  toast.error(parseErrorMessage(err, { t }));
  close();
}

function updatePlaylistName(name?: string) {
  if (playlist.value && name) {
    playlist.value = { ...playlist.value, name };
  }
}

function setCurrentTime(time: number) {
  if (!videoPlayerRef.value?.player) {
    console.warn("The current time of the video player cannot be set because the video player instance has not been initialized yet.");
    return;
  }

  videoPlayerRef.value.player.currentTime(time);
}

function removeItemsFromPlaylist(items: IPlaylistItem[]) {
  const idsSet = new Set(items.map((item) => item.id));
  const isActiveItemDeleted = idsSet.has(activePlaylistItem.value?.id || "");
  // remove deleted playlist item/s from main list to update seek buttons config and autoplay
  playlistItems.value = playlistItems.value.filter((item) => !idsSet.has(item.id));

  if (!playlistItems.value?.length) {
    // close playlist when last item was deleted
    close();

    return;
  }
  if (isActiveItemDeleted) {
    // change source if deleted tag was active
    onListItemClick(FLOATING_PLAYER_MODE.PLAYLIST, playlistItems.value[0]);
  }
}

function onUpdatePlaylistItem(updatedItem: IPlaylistItem) {
  playlistItems.value = playlistItems.value.map((item) => (item.id === updatedItem?.id ? { ...updatedItem } : item));
}

function onPlayerLoaded(data: IPlayerData) {
  // Seek to the shared time
  if (route.query.time) setCurrentTime(Number(route.query.time));

  // Execute pending player ready callbacks
  while (playerReadyCallbacks.value.length) {
    const cb = playerReadyCallbacks.value.shift();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    cb?.(data.player);
  }
}

function onPlayerUnloaded() {
  // Reset the player ready callbacks
  playerReadyCallbacks.value = [];
  telestrationStore.exitTelestrationMode();
}

function loadVideoFromQuery() {
  const accessToken = route.query.accessToken as string;

  if (route.query.videoId && route.query.videoType) {
    return getVideo(route.query.videoId as string, route.query.videoType as IBasicVideo["type"], { accessToken })
      .then((vid) => open(vid))
      .catch(onError);
  }

  if (route.query.playlistId && !route.query.reviewItems) {
    return getUserPlaylistById(route.query.playlistId as string, {
      accessToken,
      locale: locale.value,
    })
      .then((playlist) => open(playlist))
      .catch(onError);
  }

  return Promise.resolve();
}

function onVideoEnded() {
  nextVideo();
}

function onTelestrationTagTimeIntersect(evt: { data: IPixellotTag }) {
  const item = activePlaylistItem.value;
  const currentTime = playerInstance.value?.currentTime();

  if (!item || currentTime === undefined) return;

  const telestrationItem = item.telestrations?.find((t) => t.id === evt.data.id);

  if (!telestrationItem) return;

  telestrationStore.openPreviewForPlaylistItem(item, currentTime, playerInstance.value, telestrationItem);
}

const isPixellotTag = (tag: IPixellotTag | IPixellotTagGroup): tag is IPixellotTag => "id" in tag;

function onTagClick(tag: { event: PointerEvent | MouseEvent; data: IPixellotTag | IPixellotTagGroup }) {
  const item = activePlaylistItem.value;
  const currentTime = playerInstance.value?.currentTime();

  if (!item || tag.data.representation !== "telestration" || currentTime === undefined) return;

  let telestrationItem: ITelestration | undefined;

  if (isPixellotTag(tag.data)) {
    telestrationItem = item.telestrations?.find((t) => t.id === (tag.data as IPixellotTag).id);
  } else if (tag.data.tags) {
    telestrationItem = item.telestrations?.find((t) => t.id === (tag.data as IPixellotTagGroup).tags[0].id);
  } else {
    return;
  }

  if (!telestrationItem) {
    console.warn("Telestration item not found");
    return;
  }

  telestrationStore.openPreviewForPlaylistItem(item, currentTime, playerInstance.value, telestrationItem);
}

function onTelestrationButtonClick() {
  const item = activePlaylistItem.value;
  const currentTime = playerInstance.value?.currentTime();

  if (!item || currentTime === undefined) return;
  if (telestrationStore.telestrationMode === TelestrationMode.EDIT) return;

  if (telestrationStore.telestrationMode === TelestrationMode.PREVIEW) {
    telestrationStore.openEditForPlaylistItem(item, currentTime, playerInstance.value);
    return;
  }

  telestrationStore.openEditorForPlaylistItem(item, currentTime, playerInstance.value);
}

onMounted(loadVideoFromQuery);

/**
 * FIXME:
 * Sometimes there might be false "ended" events when playing the playlist items
 * e.g. the playlist item didn't loaded yet but the ended event was fired
 * this was triggering next item to be played without first even to start
 *
 * hence this solution with rebounding event listener introduced..
 * We watch for the activePlaylistItem _and_ playerInstance changes because on first load the playerInstance is not ready yet,
 * so the event listener is not attached. If we wait for the playerInstance to be ready too that means we won't miss adding the `ended` event.
 * it's a bit clunky, but at least does the job partially - we need to solve it better in future
 */
watch([activePlaylistItem, activeClippedItem, clippedItems, playerInstance], () => {
  const useEnded = (activePlaylistItem.value || (hasClippedVideoItems.value && activeClippedItem.value)) && playerInstance.value;

  if (useEnded) {
    playerInstance.value.off("ended", onVideoEnded);
    playerInstance.value.one("loadedmetadata", () => {
      setTimeout(() => {
        playerInstance.value?.one("ended", onVideoEnded);
      });
    });
  }
});

watch(
  () => isInPiPMode.value || isInNativePiPMode.value,
  (isInPiPMode) => {
    if (!video.value) return;

    if (isInPiPMode) {
      segment.track("Match Editor PiP Mode Opened", formatTrackVideo(video.value));
    } else {
      segment.track("Match Editor PiP Mode Closed", formatTrackVideo(video.value));
    }
  }
);

watch(
  () => playerInstance.value?.player_,
  (player: IPixellotPlayer | undefined) => {
    if (!player) return;

    player.on("enterpictureinpicture", () => {
      isInNativePiPMode.value = true;
    });
    player.on("leavepictureinpicture", () => {
      isInNativePiPMode.value = false;
    });
  }
);

function previousVideo() {
  if (seekButtonsConfig.value.isActive && !seekButtonsConfig.value.isPreviousDisabled) {
    return seekButtonsConfig.value.onClickPrevious();
  }

  console.warn("Previous video is not available");
}

function nextVideo() {
  if (seekButtonsConfig.value.isActive && !seekButtonsConfig.value.isNextDisabled) {
    return seekButtonsConfig.value.onClickNext();
  }

  console.warn("Next video is not available");
}

if (escapeKey) {
  whenever(escapeKey, close);
}

watch(arrowup, (value) => {
  if (!value || telestrationStore.telestrationMode === TelestrationMode.EDIT) return;
  previousVideo();
});

watch(arrowdown, (value) => {
  if (!value || telestrationStore.telestrationMode === TelestrationMode.EDIT) return;
  nextVideo();
});

function onFullscreenChange(isFullscreen: boolean) {
  telestrationStore.handleFullscreenChange(isFullscreen);
}

// Ensure singleton reference is preserved: mutate existing instance if present, otherwise provide
const existingFloatingPlayer = useFloatingPlayer();
if (existingFloatingPlayer) {
  existingFloatingPlayer.open = open;
  existingFloatingPlayer.close = close;
  existingFloatingPlayer.isDeleted = isDeleted;
  existingFloatingPlayer.video = video;
  existingFloatingPlayer.player = playerInstance;
  existingFloatingPlayer.playerState = playerState;
  existingFloatingPlayer.togglePiPMode = togglePiPMode;
} else {
  provideFloatingPlayer({
    open,
    close,
    isDeleted,
    video,
    player: playerInstance,
    playerState,
    togglePiPMode,
  });
}
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <!-- https://stackoverflow.com/questions/68998731/vue-transition-with-tailwind -->
      <Transition enter-from-class="opacity-0" leave-to-class="opacity-0"
        enter-active-class="transition-opacity duration-500" leave-active-class="transition-opacity duration-500">
        <!-- BACKGROUND -->
        <div v-if="isOpen" ref="modalRootEl" class="fixed z-30" :class="[
          isInPiPMode
            ? 'size-max touch-none select-none'
            : 'left-0 top-0 grid h-screen w-screen place-items-center overflow-auto bg-black/40 py-20',
        ]" :style="isInPiPMode ? { top: `${restrictedY}px`, left: `${restrictedX}px` } : ''"
          @click.self="isInPiPMode ? null : close()">
          <!-- POPUP -->
          <div class="rounded bg-white shadow-lg dark:bg-neutral-dark-700"
            :class="[isInPiPMode ? 'flex w-[calc(100vw-32px)] flex-col-reverse lg:w-[500px]' : (playlist || hasClippedVideoItems) ? 'w-11/12 max-w-[1320px]' : 'w-11/12 lg:w-8/12']">
            <!-- HEADER -->
            <VideoPlayerFloatingHeader v-if="isInPiPMode ? true : !playlist" ref="modalHeaderEl" :video="video"
              :playlist="playlist" :is-in-pi-p-mode="isInPiPMode" :is-in-editing-mode="isEditingPlaylistItem"
              :active-playlist-item="activePlaylistItem" :player-error="playerError" :player-state="playerState"
              :close="close" :toggle-pi-p-mode="togglePiPMode" :edit-playlist="updatePlaylistName"
              :on-delete="onDelete" />

            <!-- BODY -->
            <div :class="((playlist || hasClippedVideoItems) && !isInPiPMode) ? 'flex max-lg:flex-wrap' : ''">
              <div class="relative flex flex-col"
                :class="((playlist || hasClippedVideoItems) && !isInPiPMode) ? 'xl:basis-[50rem] lg:basis-[40rem] basis-full shrink-0' : ''">
                <LazyVideoPlayer v-if="video" ref="videoPlayerRef" class="relative w-full" :tags="telestrationTags"
                  :video="video" :video-clip="currentVideoClipOptions" :player-class="{ player__compact: isInPiPMode }"
                  :control-link="controlLink" :seek-buttons="seekButtonsConfig" quality-hls-deduplicate-levels
                  :up-next-video="!isEditingPlaylistItem
                    && telestrationStore.telestrationMode === TelestrationMode.NONE
                    && nextListItem
                    " :up-next-video-shown="!!isShowUpNextBanner" :up-next-video-category="playlist?.name ?? video?.title ?? ''"
                  :telestration-button-shown="isTelestrationButtonShown" :playlist="playlist" @mounted="onPlayerLoaded"
                  @clipped-angle-change="onClippedAngleChange"
                  @unmounted="onPlayerUnloaded" @up-next-click="() => goToNextListItem(hasClippedVideoItems ? FLOATING_PLAYER_MODE.CLIPPED : FLOATING_PLAYER_MODE.PLAYLIST)"
                  @tag-click="onTagClick($event)" @tag-time-intersect="onTelestrationTagTimeIntersect($event)"
                  @telestration-button-click="onTelestrationButtonClick()" @fullscreenchange="onFullscreenChange">
                  <template #default="{ player, source, state }">
                    <LazyVideoPlayerPiPOverlay v-if="!isDragging" :pip-active="isInPiPMode" :player="player"
                      :source="source" :state="state" @close="close()" @exit-pip="togglePiPMode(false)" />
                    <div v-if="telestrationStore.telestrationMode === TelestrationMode.EDIT"
                      class="absolute bottom-0 left-0">
                      <TTelestrationEditor />
                    </div>
                    <div v-else-if="telestrationStore.telestrationMode === TelestrationMode.PREVIEW"
                      class="-z-1 pointer-events-none absolute bottom-0 left-0">
                      <TTelestrationPreview />
                    </div>
                  </template>
                </LazyVideoPlayer>
                <div v-else class="aspect-video">
                  <RLoadingOverlay />
                </div>
                <div id="floating-player-footer" />
              </div>
              <div v-if="canShowPlaylistSidebar" class="basis-full overflow-hidden lg:grow"
                :class="[isEditingPlaylistItem ? 'hidden lg:block' : '']"
                :style="greaterOrEqualLg ? { maxHeight: `${playerState?.height}px` } : null">
                <VideoPlayerFloatingHeader v-if="!isInPiPMode" :video="video" :playlist="playlist"
                  :is-in-editing-mode="isEditingPlaylistItem" :active-playlist-item="activePlaylistItem"
                  :is-in-pi-p-mode="isInPiPMode" :player-error="playerError" :player-state="playerState" :close="close"
                  :toggle-pi-p-mode="togglePiPMode" :edit-playlist="updatePlaylistName" :on-delete="onDelete" />
                <UDivider lighter />
                <PlaylistsList v-if="playlist" :playlist="playlist" :playlist-items="playlistItems"
                  :player="playerInstance" :active-video="video" :player-state="playerState"
                  :active-item="activePlaylistItem" @toggle-edit-mode="isEditingPlaylistItem = !isEditingPlaylistItem"
                  @playlist-item-click="onListItemClick(FLOATING_PLAYER_MODE.PLAYLIST, $event)" @update-playlist-item="onUpdatePlaylistItem($event)"
                  @delete-playlist-items="removeItemsFromPlaylist($event)" @close="close()" />
              </div>
              <div v-else-if="canShowClippedSidebar" class="basis-full overflow-hidden lg:grow"
                :style="greaterOrEqualLg ? { maxHeight: `${playerState?.height}px` } : null">
                <UDivider lighter />
                <VideoClippedList :items="clippedItems" :active-item="activeClippedItem"
                  :is-video-paused="playerState?.paused" @item-click="onListItemClick(FLOATING_PLAYER_MODE.CLIPPED, $event)" />
              </div>
              <TelestrationSidePanel v-if="telestrationStore.telestrationMode === TelestrationMode.EDIT"
                :style="greaterOrEqualLg ? { maxHeight: `${playerState?.height}px` } : null"
                class="basis-full lg:grow" />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style>
.player__compact .vjs-control-bar>*:not(.vjs-progress-control) {
  display: none;
}

.player__compact .vjs-control-bar .vjs-progress-control {
  bottom: 24px;
  z-index: 20;
}

.vjs-theme-pxl.video-js .vjs-control-bar {
  z-index: 10;
}

.vjs-control-link {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
